import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "@/components/ui/use-toast";
import axiosInstance from "../../utils/axiosInstance";

// Fetch Payment Methods
export const fetchPaymentMethodsByUserId = createAsyncThunk(
  "parts/fetchPaymentMethodsByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error("User ID is required to fetch payment methods");
      }

      const response = await axiosInstance.get(`/paymentMethod/user/${userId}`);
      const { data } = response.data;

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "Error fetching payment methods";
      console.error("Fetch payment methods error:", error);
      toast({
        title: "Fetching Error",
        description: message,
        variant: "destructive",
      });
      return rejectWithValue(message);
    }
  }
);

// Add Payment Method
export const addPaymentMethod = createAsyncThunk(
  "parts/addPaymentMethod",
  async ({ payload, onSuccess = () => {} }, { rejectWithValue }) => {
    try {
      const { userId, paymentMethodId } = payload;
      if (!userId || !paymentMethodId) {
        throw new Error("Both userId and paymentMethodId are required");
      }

      const response = await axiosInstance.post("/paymentMethod", {
        userId,
        paymentMethodId,
      });
      const { data } = response.data;

      onSuccess?.();

      toast({
        title: "Payment Method Added",
        description: `Your new payment method has been saved successfully.`,
        variant: "success",
      });

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error adding your payment method. Please contact support.";

      console.error("Add payment method error:", error);

      toast({
        title: "Failed to Add Payment Method",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  }
);

// Update default Payment Method
export const updateDefaultPaymentMethod = createAsyncThunk(
  "parts/updateDefaultPaymentMethod",
  async ({ payload, onSuccess = () => {} }, { rejectWithValue }) => {
    try {
      const { userId, paymentMethodId } = payload;
      if (!userId || !paymentMethodId) {
        throw new Error("Both userId and paymentMethodId are required");
      }

      const response = await axiosInstance.put("/paymentMethod/default", {
        userId,
        paymentMethodId,
      });
      const { data } = response.data;

      onSuccess?.();

      toast({
        title: "Default Payment Method Updated",
        description:
          "Your default payment method has been changed successfully.",
        variant: "success",
      });

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error updating your default payment method. Please contact support.";

      console.error("Update default payment method error:", error);

      toast({
        title: "Failed to Update Default",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  }
);
// Remove Payment Method
export const removePaymentMethod = createAsyncThunk(
  "parts/removePaymentMethod",
  async ({ payload, onSuccess = () => {} }, { rejectWithValue }) => {
    try {
      const { userId, paymentMethodId } = payload;
      if (!userId || !paymentMethodId) {
        throw new Error("Both userId and paymentMethodId are required");
      }

      const response = await axiosInstance.delete("/paymentMethod", {
        data: {
          userId,
          paymentMethodId,
        },
      });
      const { data } = response.data;

      onSuccess?.();

      toast({
        title: "Payment Method Removed",
        description: `The selected payment method has been removed successfully.`,
        variant: "success",
      });

      return data;
    } catch (error) {
      let message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error removing your payment method. Please contact support.";

      console.error("Remove payment method error:", error);

      toast({
        title: "Failed to Remove Payment Method",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  }
);
